/**
 * Who can Sol actually serve? — the frontend mirror of the backend gate.
 *
 * `/search` is now the assistant, so this decides whether a given user gets Sol
 * or the classic deterministic search. It is NOT a permission check (the backend
 * is the authority and answers 403 on POST /api/sol/turn); it exists so a user
 * the backend would reject never lands on a dead page.
 *
 * Keep in lockstep with `api/sol_routes.py`:
 *
 *     if is_buyer_role(role):   label_tag = None          # buyers -> global catalog
 *     elif not label_tag:       raise HTTPException(403)  # everyone else needs a label
 *
 * So: buyers always qualify; every other account type needs a `label_tag`,
 * because Sol's seller path scopes every tool on it and a null label would
 * otherwise read as "the whole platform".
 *
 * Live counts when this was written (2026-07-29): 23 of 58 sellers and all 4
 * negotiators have no label — 27 users, ~28% of accounts. Routing them to the
 * classic search is why the cutover does not take search away from them.
 */

/** Mirrors backend `BUYER_ROLES` (see src/lib/roles.ts for the shared set). */
const BUYER_ROLE = "buyer";

export interface SolSubject {
  /** `profiles.role` — buyer | seller | negotiator | admin. */
  accountType: string | null;
  /** `profiles.label_tag`. */
  labelTag: string | null;
}

/**
 * True when the backend would accept a Sol turn from this user.
 *
 * Callers MUST gate on `useCapabilities().loadedOnce` before trusting this —
 * an unresolved subject looks label-less, and rendering on that would flash the
 * classic search at a user who is about to get Sol.
 */
export function canUseSol({ accountType, labelTag }: SolSubject): boolean {
  if (!accountType) return false;              // unresolved / no role -> never assume yes
  if (accountType === BUYER_ROLE) return true; // buyers need no label
  return Boolean(labelTag);                    // seller/negotiator/admin need one
}
